import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/organisms/Footer';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';
import VerificationBanner from '../components/VerificationBanner';
import VerificationPopup from '../components/VerificationPopup';
import InterviewContinuationBanner from '../components/InterviewContinuationBanner';
import LandingHeader from '../components/organisms/LandingHeader';
import SessionManager from '../utils/sessionManager';
import ProfileCompletionPrompt from '../components/ProfileCompletionPrompt';

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const counterRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => {
      if (counterRef.current) {
        observer.unobserve(counterRef.current);
      }
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    let animationFrame;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = (currentTime - startTime) / duration;

      if (progress < 1) {
        setCount(Math.floor(end * progress));
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [isVisible, end, duration]);

  return (
    <span ref={counterRef}>
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [currentSession, setCurrentSession] = useState(null);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    document.title = 'Aptirix - AI-Powered Career Platform';
    
    const session = SessionManager.getCurrentSession();
    setCurrentSession(session);

    // Redirect based on role
    if (session) {
      switch (session.role) {
        case 'hr':
          navigate('/hr/dashboard');
          return;
        case 'course_provider':
          navigate('/course-provider/dashboard');
          return;
        case 'saas_provider':
          navigate('/saas-provider/dashboard');
          return;
        case 'super_admin':
          navigate('/super-admin/dashboard');
          return;
        default:
          break;
      }
    }

    // Auto-scroll animation for features
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      let scrollAmount = 0;
      const scrollStep = 1;
      const scrollInterval = setInterval(() => {
        if (scrollContainer.scrollLeft >= scrollContainer.scrollWidth - scrollContainer.clientWidth) {
          scrollAmount = 0;
          scrollContainer.scrollLeft = 0;
        } else {
          scrollAmount += scrollStep;
          scrollContainer.scrollLeft = scrollAmount;
        }
      }, 30);

      return () => clearInterval(scrollInterval);
    }
  }, [navigate]);

  const isCandidate = currentSession?.role === 'candidate';

  const features = [
    { icon: 'psychology', title: 'AI Trainer', description: 'Practice interviews with AI feedback' },
    { icon: 'school', title: 'AI Courses', description: 'Learn with personalized AI tutoring' },
    { icon: 'work', title: 'Smart Interviews', description: 'AI-powered interview assessments' },
    { icon: 'description', title: 'Resume Builder', description: 'Create ATS-optimized resumes' },
    { icon: 'analytics', title: 'Career Analytics', description: 'Track your career progress' },
    { icon: 'verified', title: 'Skill Verification', description: 'Get verified by AI assessments' },
  ];



  return (
    <div className="min-h-screen bg-white">
      <InterviewContinuationBanner />
      <VerificationBanner />
      {isCandidate && <VerificationPopup />}
      
      {/* Profile Completion Prompt */}
      <ProfileCompletionPrompt />
      
      {/* Navigation */}
      <LandingHeader />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] text-white overflow-hidden min-h-[650px]">
        {/* Prismatic Spectrum Background - Multi-color gradient orbs */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Large center prismatic orb - spectrum effect */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/4 w-[900px] h-[900px] rounded-full bg-gradient-to-br from-cyan-400/30 via-blue-500/35 to-indigo-600/30 blur-3xl"></div>
          
          {/* Top left - Purple to Pink spectrum */}
          <div className="absolute left-0 top-0 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-purple-500/35 via-violet-500/30 to-fuchsia-500/25 blur-3xl"></div>
          
          {/* Top right - Cyan to Blue spectrum */}
          <div className="absolute right-0 top-10 w-[450px] h-[450px] rounded-full bg-gradient-to-bl from-cyan-400/30 via-sky-500/25 to-blue-600/30 blur-3xl"></div>
          
          {/* Bottom left - Blue to Teal spectrum */}
          <div className="absolute left-10 bottom-0 w-[550px] h-[550px] rounded-full bg-gradient-to-tr from-blue-600/30 via-cyan-500/25 to-teal-400/20 blur-3xl"></div>
          
          {/* Bottom right - Pink to Orange spectrum */}
          <div className="absolute right-0 bottom-0 w-[480px] h-[480px] rounded-full bg-gradient-to-tl from-pink-500/25 via-rose-500/20 to-orange-400/15 blur-3xl"></div>
          
          {/* Mid accent orbs - Adding more color variety */}
          <div className="absolute left-1/3 top-1/3 w-[300px] h-[300px] rounded-full bg-gradient-to-br from-indigo-400/25 via-purple-500/20 to-pink-500/15 blur-2xl"></div>
          <div className="absolute right-1/3 top-2/3 w-[280px] h-[280px] rounded-full bg-gradient-to-tl from-emerald-400/20 via-teal-500/15 to-cyan-500/20 blur-2xl"></div>
          
          {/* Small accent orbs for depth */}
          <div className="absolute left-1/4 bottom-1/4 w-[200px] h-[200px] rounded-full bg-gradient-to-r from-violet-400/20 to-fuchsia-400/15 blur-xl"></div>
          <div className="absolute right-1/4 top-1/4 w-[220px] h-[220px] rounded-full bg-gradient-to-l from-sky-400/20 to-blue-500/15 blur-xl"></div>
          
          {/* Subtle white highlights for shimmer effect */}
          <div className="absolute left-1/2 top-1/3 w-[180px] h-[180px] rounded-full bg-white/8 blur-2xl"></div>
          <div className="absolute right-1/5 bottom-1/3 w-[150px] h-[150px] rounded-full bg-white/6 blur-xl"></div>
        </div>
        
        {/* Subtle grid overlay */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxkZWZzPjxwYXR0ZXJuIGlkPSJncmlkIiB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiPjxwYXRoIGQ9Ik0gNDAgMCBMIDAgMCAwIDQwIiBmaWxsPSJub25lIiBzdHJva2U9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-40"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-32">
          <div className="text-center max-w-4xl mx-auto flex flex-col items-center">
            <div className="inline-block mb-6 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium border border-white/20">
              BUILT FOR CAREER SUCCESS
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight">
              AI-Powered Platform for<br />Career Excellence
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 mb-8 max-w-3xl mx-auto">
              Master interviews with AI training, enhance skills with personalized courses, 
              and unlock opportunities with intelligent job matching and career insights.
            </p>
            <div className="flex justify-center w-full">
              <Button 
                size="xl" 
                onClick={() => navigate(isCandidate ? '/ai-trainer' : '/signup')}
                className="bg-white text-gray-900 hover:bg-gray-100 px-10 py-4 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all inline-flex items-center justify-center"
              >
                {isCandidate ? 'Start AI Training' : 'Explore'}
              </Button>
            </div>
          </div>

          {/* Scrolling Feature Tags */}
          <div className="mt-16 overflow-hidden">
            <div 
              ref={scrollContainerRef}
              className="flex space-x-6 overflow-x-auto scrollbar-hide"
              style={{ scrollBehavior: 'smooth' }}
            >
              {[...features, ...features].map((feature, index) => (
                <div 
                  key={index}
                  className="flex-shrink-0 bg-white/10 backdrop-blur-md rounded-2xl px-6 py-4 flex items-center space-x-3 min-w-[280px] border border-white/20 hover:bg-white/15 transition-all"
                >
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                    <Icon name={feature.icon} size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{feature.title}</h3>
                    <p className="text-sm text-gray-200">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AI Agents Showcase Section */}
      <section id="features" className="py-12 bg-gradient-to-b from-white to-gray-50 relative overflow-visible">
        {/* Decorative top wave from hero */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#0f172a]/5 to-transparent"></div>
        
        {/* Add animation styles */}
        <style>{`
          @keyframes drawLine {
            from {
              stroke-dashoffset: 1000;
            }
            to {
              stroke-dashoffset: 0;
            }
          }
          @keyframes glowPulse {
            0%, 100% {
              filter: drop-shadow(0 0 2px rgba(59, 130, 246, 0.5)) drop-shadow(0 0 4px rgba(59, 130, 246, 0.3));
            }
            50% {
              filter: drop-shadow(0 0 4px rgba(59, 130, 246, 0.8)) drop-shadow(0 0 8px rgba(59, 130, 246, 0.5));
            }
          }
          .animate-line {
            stroke-dasharray: 1000;
            stroke-dashoffset: 1000;
            animation: drawLine 2s ease-in-out forwards;
          }
          .animate-line-glow {
            animation: glowPulse 2s ease-in-out infinite;
          }
          .animate-line-1 { animation-delay: 0.2s; }
          .animate-line-2 { animation-delay: 0.4s; }
          .animate-line-3 { animation-delay: 0.6s; }
        `}</style>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* Central Icon with connecting lines to cards */}
          <div className="flex flex-col items-center mb-8 relative">
            <div className="relative">
              <img 
                src="https://image2url.com/r2/default/images/1772794265680-ac6ce145-0cef-45a6-bb44-c1923ebf64a1.png" 
                alt="Aptirix Icon" 
                className="w-32 h-32 relative z-20"
              />
              
              {/* Animated connecting lines with glow effect */}
              <svg className="absolute top-full left-1/2 -translate-x-1/2 pointer-events-none hidden lg:block" width="1200" height="180" style={{ marginTop: '-4px' }}>
                <defs>
                  <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#3b82f6', stopOpacity: 0.9 }} />
                    <stop offset="100%" style={{ stopColor: '#60a5fa', stopOpacity: 0.6 }} />
                  </linearGradient>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                
                {/* Left line to AI Interview Trainer */}
                <path 
                  d="M 600 0 Q 600 60, 180 160" 
                  stroke="url(#lineGradient)" 
                  strokeWidth="3" 
                  fill="none"
                  filter="url(#glow)"
                  className="animate-line animate-line-1 animate-line-glow"
                />
                
                {/* Center line to Career Guidance */}
                <line 
                  x1="600" 
                  y1="0" 
                  x2="600" 
                  y2="160" 
                  stroke="url(#lineGradient)" 
                  strokeWidth="3"
                  filter="url(#glow)"
                  className="animate-line animate-line-2 animate-line-glow"
                />
                
                {/* Right line to Smart Interview */}
                <path 
                  d="M 600 0 Q 600 60, 1020 160" 
                  stroke="url(#lineGradient)" 
                  strokeWidth="3" 
                  fill="none"
                  filter="url(#glow)"
                  className="animate-line animate-line-3 animate-line-glow"
                />
              </svg>
            </div>
            
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2 text-center mt-4 relative z-10">
              Meet Aptirix's AI Agents
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl text-center relative z-10">
              Unleash powerful AI tools to accelerate your career and hiring success
            </p>
          </div>

          {/* AI Agents Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10">
            {/* AI Interview Trainer */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 group">
              <div className="p-8 pb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">AI Interview Trainer</h3>
                <p className="text-gray-600 text-sm">Practice interviews with real-time AI feedback and coaching</p>
              </div>
              
              {/* Real image with rounded corners */}
              <div className="relative h-64 overflow-hidden px-6 pb-6">
                <img 
                  src="https://img.freepik.com/free-photo/technology-integrated-everyday-life_23-2151887052.jpg?semt=ais_hybrid&w=740&q=80" 
                  alt="AI Interview Training"
                  className="w-full h-full object-cover rounded-2xl"
                />
              </div>
              
              {/* Benefits list */}
              <div className="p-8 pt-0 space-y-3">
                <div className="flex items-center space-x-2 text-sm">
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="check" size={14} className="text-green-600" />
                  </div>
                  <span className="text-gray-700">Personalized question generation</span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="check" size={14} className="text-green-600" />
                  </div>
                  <span className="text-gray-700">Instant AI feedback & scoring</span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="check" size={14} className="text-green-600" />
                  </div>
                  <span className="text-gray-700">Comprehensive PDF reports</span>
                </div>
                
                <Button 
                  onClick={() => navigate('/ai-trainer')}
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white mt-6"
                >
                  Try Now
                </Button>
              </div>
            </div>

            {/* Career Guidance Agent */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 group">
              <div className="p-8 pb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Career Guidance Agent</h3>
                <p className="text-gray-600 text-sm">AI-powered career path recommendations and skill mapping</p>
              </div>
              
              {/* Real image with rounded corners */}
              <div className="relative h-64 overflow-hidden px-6 pb-6">
                <img 
                  src="https://media.istockphoto.com/id/648946390/photo/great-school-work.jpg?s=612x612&w=0&k=20&c=vXT2l4qTUlm_RW3yl5TNKl3oRfH2aDjaN1bihid1mEA=" 
                  alt="Career Guidance"
                  className="w-full h-full object-cover rounded-2xl"
                />
              </div>
              
              <div className="p-8 pt-0 space-y-3">
                <div className="flex items-center space-x-2 text-sm">
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="check" size={14} className="text-green-600" />
                  </div>
                  <span className="text-gray-700">Personalized career roadmaps</span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="check" size={14} className="text-green-600" />
                  </div>
                  <span className="text-gray-700">Skill gap analysis & recommendations</span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="check" size={14} className="text-green-600" />
                  </div>
                  <span className="text-gray-700">Industry trend insights</span>
                </div>
                
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white mt-6"
                >
                  Learn More
                </Button>
              </div>
            </div>

            {/* Smart Interview Agent (for HR) */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 group">
              <div className="p-8 pb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Smart Interview Agent</h3>
                <p className="text-gray-600 text-sm">Automated candidate screening with AI-powered assessments</p>
              </div>
              
              {/* Real image with rounded corners */}
              <div className="relative h-64 overflow-hidden px-6 pb-6">
                <img 
                  src="https://media.istockphoto.com/id/1355302972/photo/african-american-customer-woman-talking-to-support-service-employee.jpg?s=612x612&w=0&k=20&c=oqDbA9Isz4RRdTln7BfS3yoSxN1VeY5lCRfv3u6_bAI=" 
                  alt="Smart Interview"
                  className="w-full h-full object-cover rounded-2xl"
                />
              </div>
              
              <div className="p-8 pt-0 space-y-3">
                <div className="flex items-center space-x-2 text-sm">
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="check" size={14} className="text-green-600" />
                  </div>
                  <span className="text-gray-700">Automated candidate screening</span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="check" size={14} className="text-green-600" />
                  </div>
                  <span className="text-gray-700">Detailed performance analytics</span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="check" size={14} className="text-green-600" />
                  </div>
                  <span className="text-gray-700">Bias-free candidate evaluation</span>
                </div>
                
                <Button 
                  onClick={() => navigate('/recruiter')}
                  className="w-full bg-gray-900 hover:bg-gray-800 text-white mt-6"
                >
                  Learn More
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="bg-gray-900 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-white text-center sm:text-left">
              {currentSession ? (
                <>
                  <span className="text-lg font-medium">Track your status</span>
                  <span className="text-gray-300 ml-2">View your progress and achievements</span>
                </>
              ) : (
                <>
                  <span className="text-lg font-medium">Need to boost your career status?</span>
                  <span className="text-gray-300 ml-2">Sign up and get yourself started today</span>
                </>
              )}
            </div>
            <Button 
              onClick={() => navigate(currentSession ? '/dashboard' : '/signup')}
              className="bg-white text-gray-900 hover:bg-gray-100 px-6 py-3 font-semibold whitespace-nowrap"
            >
              {currentSession ? 'Go to Dashboard' : 'Sign Up Now'}
            </Button>
          </div>
        </div>
      </section>

      {/* Why Aptirix AI Section */}
      <section className="py-20 bg-blue-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-16">
            Why Aptirix AI?
          </h2>

          {/* Feature Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {/* Card 1 - Smart Matching */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Smart Job Matching</h3>
              <p className="text-gray-600 text-sm mb-6">
                AI-powered algorithms match candidates with the perfect job opportunities based on skills, experience, and career goals.
              </p>
              <button className="text-sm font-medium text-gray-900 border border-gray-900 px-4 py-2 rounded-lg hover:bg-gray-900 hover:text-white transition-colors">
                Learn More
              </button>
            </div>

            {/* Card 2 - Resume Analysis */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Resume Analysis</h3>
              <p className="text-gray-600 text-sm mb-6">
                Advanced AI analyzes resumes to extract key skills, experience, and qualifications for accurate candidate assessment.
              </p>
              <button className="text-sm font-medium text-gray-900 border border-gray-900 px-4 py-2 rounded-lg hover:bg-gray-900 hover:text-white transition-colors">
                Learn More
              </button>
            </div>

            {/* Card 3 - Interview Insights */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Interview Insights</h3>
              <p className="text-gray-600 text-sm mb-6">
                Real-time AI feedback during interviews provides detailed performance metrics and actionable improvement suggestions.
              </p>
              <button className="text-sm font-medium text-gray-900 border border-gray-900 px-4 py-2 rounded-lg hover:bg-gray-900 hover:text-white transition-colors">
                Learn More
              </button>
            </div>

            {/* Card 4 - Career Guidance */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-all border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Career Guidance</h3>
              <p className="text-gray-600 text-sm mb-6">
                Personalized career roadmaps and skill recommendations help professionals navigate their career path effectively.
              </p>
              <button className="text-sm font-medium text-gray-900 border border-gray-900 px-4 py-2 rounded-lg hover:bg-gray-900 hover:text-white transition-colors">
                Learn More
              </button>
            </div>
          </div>

          {/* Statistics Section */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 pt-12 border-t border-gray-200">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">
                <AnimatedCounter end={10} suffix="K+" />
              </div>
              <div className="text-sm text-gray-600">Jobs Listed</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">
                <AnimatedCounter end={50} suffix="K+" />
              </div>
              <div className="text-sm text-gray-600">Resumes Analyzed</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">
                <AnimatedCounter end={95} suffix="%" />
              </div>
              <div className="text-sm text-gray-600">Match Accuracy</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">
                <AnimatedCounter end={85} suffix="%" />
              </div>
              <div className="text-sm text-gray-600">Interview Success</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">
                <AnimatedCounter end={30} suffix=" Sec" />
              </div>
              <div className="text-sm text-gray-600">Avg Response Time</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">24/7</div>
              <div className="text-sm text-gray-600">AI Availability</div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Trainer Feature Section */}
      <section className="py-20 bg-gradient-to-br from-[#0a1f5c] via-[#1e3a8a] to-[#0a1f5c] relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-white">
              <h2 className="text-4xl sm:text-5xl font-bold mb-6 leading-tight">
                Transform Your Career with Aptirix's AI Placement Trainer
              </h2>
              <p className="text-lg text-blue-100 mb-8">
                Master interview skills, get personalized feedback, and land your dream job with our advanced AI-powered training platform.
              </p>
              
              {/* Feature Tags */}
              <div className="flex flex-wrap gap-3 mb-8">
                <span className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm border border-white/20">
                  Real-time Feedback
                </span>
                <span className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm border border-white/20">
                  AI-Powered Analysis
                </span>
                <span className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm border border-white/20">
                  PDF Reports
                </span>
                <span className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm border border-white/20">
                  Placement Ready
                </span>
              </div>
              
              <Button 
                size="xl" 
                onClick={() => navigate('/ai-trainer')}
                className="bg-white text-[#0a1f5c] hover:bg-gray-100 px-8 py-4 font-semibold"
              >
                Start Training Now
              </Button>
            </div>
            
            {/* Right Image */}
            <div className="relative">
              <div className="relative z-10">
                <img 
                  src="https://image2url.com/r2/default/images/1773086732366-f838565b-5d0a-4b1d-b1fe-362b4ce8d0fb.png" 
                  alt="AI Placement Trainer"
                  className="w-full h-auto"
                />
              </div>
              {/* Glow effect behind image */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Courses Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 rounded-2xl overflow-hidden">
              <img 
                src="https://image2url.com/r2/default/images/1773086871920-b57cb1cc-8479-43aa-84f2-14b43e309154.jpg"
                alt="AI-Powered Learning"
                className="w-full h-full object-cover rounded-2xl"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                AI-Powered Learning
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Access personalized courses with AI tutoring that adapts to your learning style. 
                Get real-time assistance, interactive lessons, and track your progress with detailed analytics.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Icon name="check_circle" size={24} className="text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Personalized Learning Paths</h4>
                    <p className="text-gray-600">AI creates custom curriculum based on your goals</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="check_circle" size={24} className="text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Interactive AI Tutor</h4>
                    <p className="text-gray-600">Get instant help and explanations as you learn</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Icon name="check_circle" size={24} className="text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Progress Tracking</h4>
                    <p className="text-gray-600">Monitor your improvement with detailed analytics</p>
                  </div>
                </div>
              </div>
              <Button 
                size="lg" 
                onClick={() => {
                  navigate('/courses');
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="mt-8 bg-[#2b6cee] hover:bg-[#1a4fb8] text-white"
              >
                Explore Courses
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Ready to Transform */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            {/* Rounded Box with Gradient Background */}
            <div className="relative bg-gradient-to-br from-[#0f172a] via-[#1e3a8a] to-[#0f172a] rounded-3xl overflow-hidden p-12 lg:p-16">
              {/* Prismatic Background Effects */}
              <div className="absolute inset-0 overflow-hidden">
                {/* Large center prismatic orb */}
                <div className="absolute left-1/4 top-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-cyan-400/30 via-blue-500/35 to-indigo-600/30 blur-3xl"></div>
                
                {/* Top left - Purple to Pink spectrum */}
                <div className="absolute left-0 top-0 w-[400px] h-[400px] rounded-full bg-gradient-to-br from-purple-500/35 via-violet-500/30 to-fuchsia-500/25 blur-3xl"></div>
                
                {/* Bottom right - Blue to Cyan spectrum */}
                <div className="absolute right-0 bottom-0 w-[450px] h-[450px] rounded-full bg-gradient-to-tl from-blue-600/30 via-cyan-500/25 to-teal-400/20 blur-3xl"></div>
                
                {/* Accent orbs */}
                <div className="absolute left-1/3 bottom-1/4 w-[250px] h-[250px] rounded-full bg-gradient-to-r from-violet-400/20 to-fuchsia-400/15 blur-xl"></div>
              </div>

              <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                {/* Left Side - Content */}
                <div className="text-white">
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                    Ready to Transform Your Career?
                  </h2>
                  <p className="text-lg sm:text-xl text-gray-200 mb-8">
                    Join thousands of professionals accelerating their careers with AI
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button 
                      size="xl" 
                      onClick={() => navigate(isCandidate ? '/jobs' : '/signup')}
                      className="bg-white text-gray-900 hover:bg-gray-100 px-8 py-4 text-lg font-semibold rounded-xl shadow-lg"
                    >
                      {isCandidate ? 'Find Jobs' : 'Get Started Free'}
                    </Button>
                    <Button 
                      size="xl" 
                      variant="outline"
                      onClick={() => {
                        navigate('/courses');
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="border-2 border-white text-white hover:bg-white/10 px-8 py-4 text-lg font-semibold rounded-xl"
                    >
                      Explore Courses
                    </Button>
                  </div>
                </div>

                {/* Right Side - Image positioned to overflow */}
                <div className="relative lg:absolute lg:right-0 lg:top-1/2 lg:-translate-y-1/2 lg:w-[45%]">
                  <img 
                    src="https://image2url.com/r2/default/images/1773087358212-8acd0ea2-3317-4cdf-84ef-ea01538370ee.png"
                    alt="Professional with laptop"
                    className="w-full h-auto relative lg:-mr-12 lg:scale-110"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
